import { Box, styled } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo, useState } from "react";
import { CarTextField } from "@app/components/Inputs";
import { CarFlatPageControl } from "@app/components/FlatPageControl";
import { useSimulationModelsBoundaries } from "./useSimulationModelsBoundaries";
import { CarDownloadButton } from "@app/components/Buttons";
import { CarPageContent } from "@app/components/PageContent";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { CarExcelTable } from "@app/components/ExcelTable";
import { SimulationModelsBoundariesBoundaryChart } from "./SimulationModelsBoundariesBoundaryChart";
import { SimulationModelsBoundariesBoundaryTable } from "./SimulationModelsBoundariesBoundaryTable";
import { SimulationModelsBoundariesGrowthDataTable } from "./SimulationModelsBoundariesGrowthDataTable";
import { SimulationModelsBoundariesGrowthDataChart } from "./SimulationModelsBoundariesGrowthDataChart";
import { SimulationModelsBoundariesBoxAndWhiskerChart } from "./SimulationModelsBoundariesBoxAndWhiskerChart";

const StyledBox = styled(Box)(({ theme }) => ({
  border: "solid 1px",
  borderColor: theme.palette.gray6,
  borderRadius: "5px",
  backgroundColor: theme.palette.white,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(5.5),
}));

interface SimulationModelsBoundariesProps {}

export const SimulationModelsBoundaries = memo(
  (props: SimulationModelsBoundariesProps) => {
    const [currentBoundaryTab, setCurrentBoundaryTab] = useState(0);
    const [currentTab, setCurrentTab] = useState(0);

    const pageContent = usePageContent();

    const data = useSimulationModelsBoundaries();

    return (
      <TablePlaceHolder
        sx={{ mx: 7, mt: 10 }}
        isLoading={data.isLoading || pageContent.isLoading}
        rows={8}
      >
        <CarPageContent
          sx={{ mb: 3 }}
          content={pageContent.getContent(
            CarPageContentCode.SIMULATION_ANALYSIS_MODELS_BOUNDARY,
          )}
          isWhiteContext
        />
        <Box sx={{ mt: 2, display: "flex" }}>
          <CarAssetClassSelector
            sx={{
              width: 300,
              ".MuiInputBase-input": {
                backgroundColor: "white",
              },
            }}
            label="Asset Class"
            {...data.assetClassSelector}
          />
          <CarDownloadButton
            sx={{ ml: "auto", mb: 0.5 }}
            onClick={data.handleDownloadReports}
          >
            Download Reports
          </CarDownloadButton>
        </Box>
        <TablePlaceHolder
          sx={{ mx: 7, mt: 10 }}
          isLoading={data.isLoading}
          rows={8}
        >
          <CarTextField
            sx={{
              mt: 6.5,
              width: 610,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
            fullWidth
            label="Calculation Notes"
            multiline
            maxRows={4}
            rows={4}
            InputProps={{
              readOnly: true,
            }}
            value={data.notes}
            onChange={() => {}}
          />
          <CarFlatPageControl
            sx={{
              mt: 5,
              ".flat-page-control-header": {
                ml: 3,
                gap: 3,
              },
              ".flat-page-control-button": {
                p: 1,
                fontSize: "18px",
              },
            }}
            items={[
              {
                label: "Boundary Table",
                value: 0,
                content: (
                  <StyledBox>
                    <SimulationModelsBoundariesBoundaryTable
                      items={data.boundaryItems}
                    />
                  </StyledBox>
                ),
              },
              {
                label: "Boundary Chart",
                value: 1,
                content: (
                  <StyledBox>
                    <SimulationModelsBoundariesBoundaryChart
                      items={data.boundaryItems}
                      isWhiteContext
                    />
                  </StyledBox>
                ),
              },
              {
                label: "Growth Data Table",
                value: 2,
                content: (
                  <StyledBox>
                    <SimulationModelsBoundariesGrowthDataTable
                      items={data.growthDataItems}
                    />
                  </StyledBox>
                ),
              },
              {
                label: "Growth Data Chart",
                value: 3,
                content: (
                  <StyledBox>
                    <SimulationModelsBoundariesGrowthDataChart
                      items={data.growthDataItems}
                      isWhiteContext
                    />
                  </StyledBox>
                ),
              },
              {
                label: "Box & Whisker",
                value: 4,
                content: (
                  <StyledBox>
                    <SimulationModelsBoundariesBoxAndWhiskerChart
                      items={data.forwardReturnsItems}
                      isWhiteContext
                    />
                  </StyledBox>
                ),
              },
            ]}
            value={currentBoundaryTab}
            onChange={setCurrentBoundaryTab}
          />
          <CarFlatPageControl
            sx={{
              mt: 5,
              ".flat-page-control-header": {
                ml: 3,
                gap: 3,
              },
              ".flat-page-control-button": {
                p: 1,
                fontSize: "18px",
              },
            }}
            items={data.tables.map((i, idx) => ({
              label: i.name,
              value: idx,
              content: (
                <StyledBox>
                  <CarExcelTable table={i} />
                </StyledBox>
              ),
            }))}
            value={currentTab}
            onChange={setCurrentTab}
          />
        </TablePlaceHolder>
      </TablePlaceHolder>
    );
  },
);
